/**
 * Darstellung des MSM-Partner-Dashboards — ohne Datenabruf, ohne Sitzung, ohne Router.
 * Zwei Abnehmer: die Seite bei uns (index.tsx) und der Spiegel im MSM-Cockpit (embed.tsx).
 * Vertrag der Daten: node-app/src/services/msmDelivery/msmPartnerTypes.ts.
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, ReactNode, useMemo } from 'react';
import { CampaignsSection } from './CampaignsSection';
import { DeliveriesChart, ListSizeChart } from './charts';
import { berlin, C, Card, Chip, eur, FONT, int, monthLabel, pct, Progress, StageBar, TableWrap, Tile } from './ui';
import type { MsmPartnerOverview, StageSummary } from './types';

const offen = (s: StageSummary) => s.waitingFirstMail + s.checkRunning + s.resultPending;
const nichtAbrechenbar = (s: StageSummary) => s.unsub + s.bounce + s.other;

interface Props {
  data: MsmPartnerOverview | null;
  fehler: boolean;
  /** Rechts im Kopf (bei uns: Ruecksprung + Abmelden; im Cockpit leer). */
  headerActions?: ReactNode;
  /** Eingebettet in eine fremde Seite: kein klebender Kopf, keine Mindesthoehe. */
  embedded?: boolean;
  /** Kopfzeile; bei uns aus Finanztrends-Sicht, im Cockpit aus MSM-Sicht (embed.tsx). */
  title?: string;
  subtitle?: string;
  /** Das FT-Zeichen links im Kopf — im MSM-Cockpit wirkte es wie ein fremdes Logo. */
  showBrandMark?: boolean;
}

export const PartnerDashboardView: FC<Props> = ({
  data,
  fehler,
  headerActions,
  embedded = false,
  title = 'Lead-Lieferung an MSM',
  subtitle = 'Partner-Dashboard · bereitgestellt von Finanztrends',
  showBrandMark = true,
}) => {
  const names = useMemo(() => Object.fromEntries((data?.lists ?? []).map((l) => [l.account, l.name])), [data]);
  const latestSize = useMemo(() => {
    const m = new Map<string, MsmPartnerOverview['listSizeDaily'][number]>();
    for (const r of data?.listSizeDaily ?? []) {
      const cur = m.get(r.account);
      if (!cur || r.day > cur.day) m.set(r.account, r);
    }
    return m;
  }, [data]);

  return (
    <Box sx={{ flex: 1, minWidth: 0, minHeight: embedded ? 'auto' : '100vh', bgcolor: C.bg, fontFamily: FONT }}>
      {/* Kopf */}
      <Box sx={{ bgcolor: C.card, borderBottom: `1px solid ${C.line}`, ...(embedded ? {} : { position: 'sticky', top: 0, zIndex: 10 }) }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {showBrandMark && (
            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: C.brand, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
              FT
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>{title}</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.ink2 }}>{subtitle}</Typography>
          </Box>
          {headerActions}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, display: 'grid', gap: { xs: 2, md: 2.5 } }}>
        {fehler && (
          <Card title="Daten derzeit nicht verfügbar">
            <Typography sx={{ color: C.ink2, fontSize: 14 }}>Bitte versuchen Sie es in einigen Minuten erneut.</Typography>
          </Card>
        )}
        {!data && !fehler && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
            <CircularProgress size={32} sx={{ color: C.brand }} />
          </Box>
        )}
        {data && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', color: C.ink2, fontSize: 13 }}>
              <span>Stand: {berlin(data.asOf.generatedAtUtc)}</span>
              <span style={{ color: C.ink3 }}>·</span>
              <span>Ergebnisstand: Abgleich {berlin(data.asOf.statusSyncedAtUtc)}</span>
              <span style={{ color: C.ink3 }}>·</span>
              <span>Versandberichte: {berlin(data.asOf.campaignsUpdatedAtUtc)}</span>
            </Box>

            <Box sx={{ bgcolor: C.brandSoft, border: `1px solid #cfe1f3`, borderRadius: '14px', px: { xs: 2, md: 2.5 }, py: 1.75, color: C.ink, fontSize: 14, lineHeight: 1.5 }}>
              <b>So wird abgerechnet:</b> Abrechenbar ist ein gelieferter Lead, der <b>48 Stunden nach seiner ersten MSM-Mail</b> noch
              angemeldet ist. Das Ergebnis wird einmal täglich festgeschrieben und danach nicht mehr geändert. Abrechnungsmonat ist der
              Liefermonat; ein Monat ist abgeschlossen, sobald er vorbei ist und alle seine Leads entschieden sind.
            </Box>

            {/* Kacheln */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: { xs: 1.5, md: 2 } }}>
              <Tile
                label="Geliefert"
                value={int(data.totals.delivered)}
                sub={`Prio 1: ${int(data.totalsByPrio.prio1.delivered)} · Prio 2: ${int(data.totalsByPrio.prio2.delivered)}`}
              />
              <Tile
                label="Abrechenbar (final)"
                value={eur(data.totals.billableEur)}
                accent={C.billable}
                sub={`${int(data.totals.billable)} Leads · ${pct(data.totals.billable, data.totals.billable + nichtAbrechenbar(data.totals))} der entschiedenen`}
              />
              <Tile
                label="Noch offen"
                value={int(offen(data.totals))}
                accent={C.checkRunning}
                sub={`bis zu ${eur(data.totals.openMaxEur)} · ${int(data.totals.waitingFirstMail)} warten auf die erste MSM-Mail`}
              />
              <Tile
                label="Nicht abrechenbar"
                value={int(nichtAbrechenbar(data.totals))}
                accent={C.unsub}
                sub={`${int(data.totals.unsub)} abgemeldet · ${int(data.totals.bounce)} unzustellbar · ${int(data.totals.other)} sonstige`}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' }, gap: { xs: 2, md: 2.5 } }}>
              <Card title="Ergebnis aller gelieferten Leads" subtitle="Je Lead genau eine Kategorie; Summe = geliefert.">
                <StageBar s={data.totals} />
              </Card>
              <Card title="Vertragsfortschritt" subtitle="Gelieferte Leads gegen die vereinbarten Mengen.">
                <Box sx={{ display: 'grid', gap: 2.5 }}>
                  <Progress label="Prio 1" value={data.contract.prio1.delivered} total={data.contract.prio1.total} color={C.prio1} />
                  <Progress label="Prio 2" value={data.contract.prio2.delivered} total={data.contract.prio2.total} color={C.prio2} />
                </Box>
              </Card>
            </Box>

            {/* Abrechnung */}
            <Card title="Abrechnung je Liefermonat" subtitle="Euro aus dem vereinbarten Preis je gelieferten Lead. „Offen“ kann noch abrechenbar werden.">
              <TableWrap>
                <table>
                  <thead>
                    <tr>
                      <th>Liefermonat</th>
                      <th>Prio</th>
                      <th>Geliefert</th>
                      <th>Abrechenbar</th>
                      <th>Nicht abrechenbar</th>
                      <th>Offen</th>
                      <th>Betrag (final)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.billingMonths.map((m) => (
                      <tr key={`${m.month}/${m.prio}`}>
                        <td style={{ fontWeight: 600 }}>{monthLabel(m.month)}</td>
                        <td>Prio {m.prio}</td>
                        <td>{int(m.delivered)}</td>
                        <td style={{ color: C.billable, fontWeight: 600 }}>{int(m.billable)}</td>
                        <td>{int(nichtAbrechenbar(m))}</td>
                        <td>{int(offen(m))}</td>
                        <td style={{ fontWeight: 700 }}>{eur(m.billableEur)}</td>
                        <td>
                          {m.closed ? (
                            <Chip color="#0a6b37" bg="#e3f6ea">abgeschlossen</Chip>
                          ) : (
                            <Chip color="#1d4ed8" bg="#e6efff">läuft</Chip>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{ fontWeight: 700 }}>Gesamt</td>
                      <td />
                      <td style={{ fontWeight: 700 }}>{int(data.totals.delivered)}</td>
                      <td style={{ fontWeight: 700, color: C.billable }}>{int(data.totals.billable)}</td>
                      <td style={{ fontWeight: 700 }}>{int(nichtAbrechenbar(data.totals))}</td>
                      <td style={{ fontWeight: 700 }}>{int(offen(data.totals))}</td>
                      <td style={{ fontWeight: 700 }}>{eur(data.totals.billableEur)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </TableWrap>
            </Card>

            {/* Lieferungen */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: { xs: 2, md: 2.5 } }}>
              <Card title="Lieferungen je Tag" subtitle="Gelieferte Leads, letzte 60 Tage.">
                <DeliveriesChart days={data.deliveriesDaily} />
              </Card>
              <Card title="Je Liste" subtitle="Quote = abrechenbar unter den bereits entschiedenen Leads.">
                <TableWrap>
                  <table>
                    <thead>
                      <tr>
                        <th>Liste</th>
                        <th>Prio 1</th>
                        <th>Prio 2</th>
                        <th>Abrechenbar</th>
                        <th>Offen</th>
                        <th>Quote</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lists.map((l) => (
                        <tr key={l.account}>
                          <td style={{ fontWeight: 600 }}>{l.name}</td>
                          <td>{int(l.prio1)}</td>
                          <td>{int(l.prio2)}</td>
                          <td>{int(l.billable)}</td>
                          <td>{int(offen(l))}</td>
                          <td>{pct(l.billable, l.billable + nichtAbrechenbar(l))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
              </Card>
            </Box>

            <CampaignsSection campaigns={data.campaigns} firstDeliveryUtc={data.asOf.firstDeliveryUtc} />

            {/* Listengroesse — gleiche Spalten: mit Systemschrift (Cockpit-Spiegel) braucht die Tabelle rechts ~512 px */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: { xs: 2, md: 2.5 } }}>
              <Card title="Listengröße" subtitle="Angemeldete Kontakte je Liste laut Mailchimp.">
                <ListSizeChart monthly={data.listGrowthMonthly} daily={data.listSizeDaily} lists={data.lists} names={names} />
              </Card>
              <Card
                title="Aktuell je Liste"
                subtitle={`Angemeldet laut Mailchimp (${berlin(data.asOf.listsUpdatedAtUtc)}). Neue Kampagne = seit ${berlin(data.asOf.firstDeliveryUtc, false)} gelieferte, noch angemeldete Leads; Bestandsleads = alle übrigen.`}
              >
                <TableWrap>
                  <table>
                    <thead>
                      <tr>
                        <th>Liste</th>
                        <th>Bestandsleads</th>
                        <th>Neue Kampagne</th>
                        <th>Gesamt</th>
                        <th>Anteil neu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.lists.map((l) => {
                        const s = latestSize.get(l.account);
                        const neu = s?.oursSubscribed ?? null;
                        const bestand = s && neu !== null ? Math.max(0, s.memberCount - neu) : null;
                        return (
                          <tr key={l.account}>
                            <td style={{ fontWeight: 600 }}>{l.name}</td>
                            <td>{int(bestand)}</td>
                            <td>{int(neu)}</td>
                            <td>{int(s?.memberCount)}</td>
                            <td>{pct(neu, s?.memberCount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableWrap>
              </Card>
            </Box>

            <Typography sx={{ fontSize: 12, color: C.ink3, textAlign: 'center', py: 2 }}>
              Daten: Mailchimp-Berichte der MSM-Listen und Lieferprotokoll von Finanztrends · erstellt {berlin(data.asOf.generatedAtUtc)}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
